import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText, ShieldCheck, Archive, X, Send } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/operator/services')({
  staticData: { crumb: 'Biblioteka usług' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: OperatorServices,
});

type ServiceState = 'draft' | 'pending_review' | 'approved' | 'archived';

interface ServiceListRow {
  id: string;
  vertical: string;
  slug: string;
  state: ServiceState;
  reviewed_by: string | null;
  reviewed_at: number | null;
  notes: string | null;
  updated_at: number;
  created_at: number;
}

interface ServiceDetail extends ServiceListRow {
  schema: Record<string, unknown>;
  schema_json: string;
}

const STATE_LABEL: Record<ServiceState, string> = {
  draft: 'Szkic',
  pending_review: 'Czeka na recenzję',
  approved: 'Zatwierdzona',
  archived: 'Zarchiwizowana',
};

const VERTICALS = ['vet', 'medical'] as const;

function OperatorServices() {
  const [vertical, setVertical] = React.useState<string>('vet');
  const [stateFilter, setStateFilter] = React.useState<ServiceState | ''>('');
  const [editing, setEditing] = React.useState<{ vertical: string; slug: string } | 'new' | null>(
    null
  );

  const list = useQuery({
    queryKey: ['operator', 'services', vertical, stateFilter],
    queryFn: async (): Promise<ServiceListRow[]> => {
      const url = new URL(`${env.apiBaseUrl}/api/operator/services`, window.location.origin);
      url.searchParams.set('vertical', vertical);
      if (stateFilter) url.searchParams.set('state', stateFilter);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error('failed_to_load');
      const j = (await res.json()) as { rows: ServiceListRow[] };
      return j.rows;
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
            Biblioteka usług
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            Edycja per-wertykalnych definicji usług medycznych. Każda nowa
            wpis przechodzi przez recenzję lekarza (KIWL/PWZ) przed zatwierdzeniem.
          </p>
        </div>
        <Button type="button" onClick={() => setEditing('new')}>
          <Plus className="size-4" aria-hidden="true" /> Nowa usługa
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-md bg-surface p-1 ring-1 ring-black/5">
          {VERTICALS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVertical(v)}
              className={cn(
                'rounded px-3 py-1 text-xs font-semibold uppercase tracking-wide transition',
                vertical === v
                  ? 'bg-bg text-text shadow-sm ring-1 ring-black/5'
                  : 'text-text-muted hover:text-text'
              )}
            >
              {v}
            </button>
          ))}
        </div>
        <Field label="Status" htmlFor="state">
          <Select
            id="state"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as ServiceState | '')}
            className="h-9 w-48"
          >
            <option value="">— wszystkie —</option>
            {(Object.keys(STATE_LABEL) as ServiceState[]).map((s) => (
              <option key={s} value={s}>
                {STATE_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {list.data ? `${list.data.length} usług` : 'Ładowanie…'}
          </CardTitle>
          <CardDescription>
            Live render czyta z plików YAML w repo. Wpisy w tej tabeli to wersje
            robocze + cykl recenzji. Po zatwierdzeniu zsynchronizuj YAML ręcznie
            (auto-PR jest follow-upem).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {list.data && list.data.length > 0 ? (
            <ul role="list" className="divide-y divide-border">
              {list.data.map((s) => (
                <li
                  key={s.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-surface/60"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-baseline gap-2">
                      <span className="font-display text-sm font-semibold text-text">{s.slug}</span>
                      <StateBadge state={s.state} />
                    </p>
                    <p className="mt-0.5 text-xs text-text-muted">
                      {s.vertical} · zaktualizowane{' '}
                      {new Date(s.updated_at * 1000).toLocaleString('pl-PL')}
                      {s.reviewed_by ? ` · zrecenzowane przez ${s.reviewed_by}` : ''}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing({ vertical: s.vertical, slug: s.slug })}
                  >
                    Otwórz
                  </Button>
                </li>
              ))}
            </ul>
          ) : list.isPending ? null : (
            <p className="p-4 text-sm text-text-muted">
              Brak wpisów w tej kombinacji filtrów. Dodaj pierwszą usługę.
            </p>
          )}
        </CardContent>
      </Card>

      {editing ? (
        <ServiceEditor
          target={editing}
          defaultVertical={vertical}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function StateBadge({ state }: { state: ServiceState }) {
  const cls: Record<ServiceState, string> = {
    draft: 'bg-surface text-text-muted ring-border',
    pending_review: 'bg-warning/10 text-warning ring-warning/20',
    approved: 'bg-success/10 text-success ring-success/20',
    archived: 'bg-surface text-text-subtle ring-border',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[10px] ring-1', cls[state])}>
      {STATE_LABEL[state]}
    </span>
  );
}

function ServiceEditor({
  target,
  defaultVertical,
  onClose,
}: {
  target: { vertical: string; slug: string } | 'new';
  defaultVertical: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isNew = target === 'new';
  const detail = useQuery({
    queryKey: isNew ? ['operator', 'services', 'new'] : ['operator', 'service', target],
    enabled: !isNew,
    queryFn: async (): Promise<ServiceDetail | null> => {
      if (isNew) return null;
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/services/${target.vertical}/${target.slug}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('failed_to_load');
      return (await res.json()) as ServiceDetail;
    },
  });

  const [vertical, setVertical] = React.useState(isNew ? defaultVertical : target.vertical);
  const [slug, setSlug] = React.useState(isNew ? '' : target.slug);
  const [schemaText, setSchemaText] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [reviewedBy, setReviewedBy] = React.useState('');
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (detail.data) {
      setSchemaText(JSON.stringify(detail.data.schema, null, 2));
      setNotes(detail.data.notes ?? '');
    }
  }, [detail.data]);

  const save = useMutation({
    mutationFn: async () => {
      setErr(null);
      let schema: unknown;
      try {
        schema = JSON.parse(schemaText);
      } catch {
        throw new Error('Schema JSON nieprawidłowy.');
      }
      const res = await fetch(`${env.apiBaseUrl}/api/operator/services`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ vertical, slug, schema, notes: notes || undefined }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'save_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'services'] });
      qc.invalidateQueries({ queryKey: ['operator', 'service'] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  const submit = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/services/${vertical}/${slug}/submit`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error('submit_failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'services'] });
      qc.invalidateQueries({ queryKey: ['operator', 'service'] });
    },
  });

  const approve = useMutation({
    mutationFn: async () => {
      if (!reviewedBy) throw new Error('Wpisz KIWL/PWZ recenzenta.');
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/services/${vertical}/${slug}/approve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ reviewed_by: reviewedBy }),
        }
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'approve_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'services'] });
      qc.invalidateQueries({ queryKey: ['operator', 'service'] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  const archive = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/services/${vertical}/${slug}/archive`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error('archive_failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'services'] });
      onClose();
    },
  });

  const state: ServiceState = detail.data?.state ?? 'draft';
  const canEdit = state !== 'approved' && state !== 'archived';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-text/40 p-4"
    >
      <div className="relative max-h-[90vh] w-full max-w-3xl space-y-4 overflow-y-auto rounded-[var(--radius-card)] bg-bg p-6 shadow-xl ring-1 ring-black/10">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-text-muted hover:text-text"
          onClick={onClose}
          aria-label="Zamknij"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
        <div className="flex items-baseline gap-2">
          <h2 className="font-display text-lg font-semibold text-text">
            {isNew ? 'Nowa usługa' : `${target.vertical}/${target.slug}`}
          </h2>
          {!isNew ? <StateBadge state={state} /> : null}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Wertykał" htmlFor="vert">
            <Select
              id="vert"
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              disabled={!isNew}
            >
              {VERTICALS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Slug" htmlFor="slug" hint="lowercase + myślniki, 3-60 znaków">
            <Input
              id="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={!isNew}
              className="font-mono text-xs"
            />
          </Field>
        </div>

        <Field
          label="Schema (JSON)"
          htmlFor="schema"
          hint="Pełne pole Service — { slug, name_pl, category, short_description, indications, contraindications, … }"
        >
          <Textarea
            id="schema"
            rows={16}
            value={schemaText}
            onChange={(e) => setSchemaText(e.target.value)}
            disabled={!canEdit}
            className="font-mono text-[11px] leading-relaxed"
            placeholder={'{\n  "slug": "...",\n  "name_pl": "...",\n  ...\n}'}
          />
        </Field>

        <Field label="Notatki" htmlFor="notes">
          <Textarea
            id="notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canEdit}
            placeholder="Skąd ta wersja? Co zmieniono?"
          />
        </Field>

        {!canEdit ? (
          <p className="rounded-md bg-warning/10 p-3 text-xs text-warning ring-1 ring-warning/20">
            {state === 'approved'
              ? 'Wpis zatwierdzony. Aby edytować, wprowadź zmiany — stan wróci do „draft" i konieczna będzie nowa recenzja.'
              : 'Wpis zarchiwizowany. Edycja zablokowana.'}
          </p>
        ) : null}

        {err ? (
          <p className="rounded-md bg-error/10 px-3 py-2 text-sm text-error ring-1 ring-error/20">
            {err}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <Button type="button" onClick={() => save.mutate()} disabled={save.isPending || !canEdit}>
            <FileText className="size-4" aria-hidden="true" /> Zapisz szkic
          </Button>

          {state === 'draft' ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => submit.mutate()}
              disabled={submit.isPending}
            >
              <Send className="size-4" aria-hidden="true" /> Do recenzji
            </Button>
          ) : null}

          {state === 'pending_review' ? (
            <>
              <Input
                placeholder="KIWL / PWZ recenzenta"
                value={reviewedBy}
                onChange={(e) => setReviewedBy(e.target.value)}
                className="h-9 w-56"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => approve.mutate()}
                disabled={approve.isPending || !reviewedBy}
              >
                <ShieldCheck className="size-4" aria-hidden="true" /> Zatwierdź
              </Button>
            </>
          ) : null}

          {!isNew && state !== 'archived' ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (confirm('Zarchiwizować ten wpis?')) archive.mutate();
              }}
              disabled={archive.isPending}
              className="ml-auto"
            >
              <Archive className="size-4" aria-hidden="true" /> Archiwizuj
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
